// src/components/OrderDetails/DesktopOrderDetailsView.js
import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Divider,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocalShipping as LocalShippingIcon,
  LocationCity as LocationCityIcon,
  LocationOn as LocationOnIcon,
  Notes as NotesIcon,
  Payment as PaymentIcon,
  ShoppingBag as ShoppingBagIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

const DesktopOrderDetailsView = ({ 
  order, 
  updating, 
  updateOrderStatus, 
  getStatusDetails, 
  shippingAddress, 
  subtotal, 
  shippingPrice,
  getProductImage,
  getProductName,
  getProductColor
}) => {
  const statusDetails = getStatusDetails(order.status);
  
  return (
    <Grid container spacing={{ xs: 2, sm: 3 }}>
      <Grid item xs={12} md={8}>
        {/* Order Items - Table version */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, boxShadow: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <ShoppingBagIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              المنتجات ({order.order_items?.length || 0})
            </Typography>
          </Box>

          <TableContainer 
            sx={{ 
              direction: 'rtl',
              '& .MuiTable-root': {
                direction: 'rtl',
                minWidth: 650,
                borderCollapse: 'separate',
                borderSpacing: 0
              },
              // Enhanced mobile responsiveness
              '@media (max-width: 900px)': {
                '& .MuiTable-root': {
                  minWidth: 'auto'
                }
              },
              // Horizontal scroll on very small screens if needed
              overflowX: 'auto',
              mb: 2
            }}
          >
            <Table sx={{ direction: 'rtl' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{
                    textAlign: 'center',
                    direction: 'rtl',
                    padding: '16px 24px',
                    fontWeight: 'bold',
                    color: 'white',
                    backgroundColor: 'primary.main',
                    borderTopRightRadius: '8px',
                    '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                    '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                  }}>
                    المنتج
                  </TableCell>
                  <TableCell sx={{
                    textAlign: 'center',
                    direction: 'rtl',
                    padding: '16px 24px',
                    fontWeight: 'bold',
                    color: 'white',
                    backgroundColor: 'primary.main',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                    '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                  }}>
                    الوصف
                  </TableCell>
                  <TableCell sx={{
                    textAlign: 'center',
                    direction: 'rtl',
                    padding: '16px 24px',
                    fontWeight: 'bold',
                    color: 'white',
                    backgroundColor: 'primary.main',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                    '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                  }}>
                    السعر
                  </TableCell>
                  <TableCell sx={{
                    textAlign: 'center',
                    direction: 'rtl',
                    padding: '16px 24px',
                    fontWeight: 'bold',
                    color: 'white',
                    backgroundColor: 'primary.main',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                    '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                  }}>
                    الكمية
                  </TableCell>
                  <TableCell sx={{
                    textAlign: 'center',
                    direction: 'rtl',
                    padding: '16px 24px',
                    fontWeight: 'bold',
                    color: 'white',
                    backgroundColor: 'primary.main',
                    borderTopLeftRadius: '8px',
                    borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                    '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                    '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                  }}>
                    الإجمالي
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {order.order_items?.map((item, idx) => (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{
                      textAlign: 'center',
                      direction: 'rtl',
                      padding: '16px 24px',
                      '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                      '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Box
                          component="img"
                          src={getProductImage(item) || '/placeholder.png'}
                          alt={getProductName(item)}
                          sx={{ 
                            width: { xs: 50, sm: 60, md: 80 }, 
                            height: { xs: 50, sm: 60, md: 80 }, 
                            objectFit: 'cover', 
                            borderRadius: 1
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{
                      textAlign: 'center',
                      direction: 'rtl',
                      padding: '16px 24px',
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                      '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                    }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 600, 
                        mb: 1,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}>
                        {getProductName(item)}
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        gap: { xs: 0.5, sm: 1 }, 
                        flexWrap: 'wrap',
                        justifyContent: 'center'
                      }}>
                        <Chip 
                          label={`اللون: ${getProductColor(item)}`} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ 
                            fontWeight: 'bold',
                            fontSize: { xs: '0.65rem', sm: '0.75rem' },
                            height: { xs: 20, sm: 24 }
                          }}
                        />
                        {item.size && (
                          <Chip 
                            label={`المقاس: ${item.size}`} 
                            size="small" 
                            color="secondary" 
                            variant="outlined"
                            sx={{ 
                              fontWeight: 'bold',
                              fontSize: { xs: '0.65rem', sm: '0.75rem' },
                              height: { xs: 20, sm: 24 }
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{
                      textAlign: 'center',
                      direction: 'rtl',
                      padding: '16px 24px',
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                      '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                    }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}>
                        {item.price_at_purchase.toFixed(2)} ج.م
                      </Typography>
                    </TableCell>
                    <TableCell sx={{
                      textAlign: 'center',
                      direction: 'rtl',
                      padding: '16px 24px',
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                      '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                    }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}>
                        {item.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{
                      textAlign: 'center',
                      direction: 'rtl',
                      padding: '16px 24px',
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                      '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                    }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 700,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}>
                        {(item.quantity * item.price_at_purchase).toFixed(2)} ج.م
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Order Summary with Breakdown */}
          <Box sx={{ backgroundColor: 'grey.50', p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary" fontWeight="bold">المجموع الفرعي للمنتجات:</Typography>
              <Typography variant="body2" fontWeight="bold">{subtotal.toFixed(2)} جنيه</Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocalShippingIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" fontWeight="bold">مصاريف الشحن:</Typography>
              </Box>
              <Typography variant="body2" fontWeight="bold" color="info.main">
                {shippingPrice.toFixed(2)} جنيه
              </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="bold">المجموع الكلي:</Typography>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {order.total_amount.toFixed(2)} جنيه
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Grid>

      {/* Right Column: Order Status, Payment Method, Shipping, and Payment Screenshot */}
      <Grid item xs={12} md={4}>
        <Stack spacing={{ xs: 2, sm: 3 }}>
          {/* Order Status without Admin Controls */}
          <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, boxShadow: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                حالة الطلب
              </Typography>
              <Chip
                icon={statusDetails.icon}
                label={statusDetails.label}
                color={statusDetails.color}
                sx={{ 
                  fontSize: { xs: '0.8rem', sm: '1rem' }, 
                  p: { xs: 1, sm: 2 }, 
                    '& .MuiChip-icon': { fontSize: { xs: '1rem', sm: '1.2rem' } },
                    '& .MuiChip-label': { fontWeight: 'bold' }
                }}
                variant="outlined"
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <Typography variant="body2" color="text.secondary" fontWeight="bold">تاريخ الطلب:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {new Date(order.created_at).toLocaleDateString('ar-EG')}
              </Typography>
            </Box>

            {order.updated_at && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary" fontWeight="bold">آخر تحديث:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {new Date(order.updated_at).toLocaleDateString('ar-EG')}
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Payment Method Section - Now with Accordion */}
          <Paper sx={{ borderRadius: 3, boxShadow: 3 }}>
            <Accordion 
              defaultExpanded={!order.payment_screenshot}
              sx={{ 
                boxShadow: 'none',
                '&:before': { display: 'none' },
                borderRadius: 3
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  p: { xs: 1.5, sm: 2 }, // Reduced vertical padding
                  pb: 0.5, // Extra bottom padding reduction
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    my: 0.5 // Reduced vertical margin on content
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaymentIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    طريقة الدفع
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: { xs: 1.5, sm: 2 }, pt: 0, mt: -0.5 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1, mt: -0.5 }}>
                  {order.payment_method === 'vodafone_cash'
                    ? 'فودافون كاش'
                    : order.payment_method === 'cash_on_delivery'
                      ? 'الدفع عند الاستلام'
                      : order.payment_method}
                </Typography>
                
                {/* Payment Screenshot in the same accordion */}
                {order.payment_screenshot && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                      إيصال الدفع:
                    </Typography>
                    <Box sx={{ border: '2px dashed', borderColor: 'divider', borderRadius: 2, p: 1, textAlign: 'center' }}>
                      <img
                        src={`${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/payment-screenshots/${order.user_id}/${order.payment_screenshot}`}
                        alt="Payment screenshot"
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: window.innerWidth < 600 ? 200 : 300, 
                          borderRadius: 8 
                        }}
                        onError={(e) => {
                          e.target.src = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/payment-screenshots/${order.payment_screenshot}`;
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Paper>

          {/* Shipping Address Section - Now with Accordion */}
          {shippingAddress && (
            <Paper sx={{ borderRadius: 3, boxShadow: 3 }}>
              <Accordion 
                defaultExpanded={true}
                sx={{ 
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  borderRadius: 3
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ 
                    p: { xs: 1.5, sm: 2 }, // Reduced vertical padding
                    pb: 0.5, // Extra bottom padding reduction
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      my: 0.5 // Reduced vertical margin on content
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShippingIcon color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      معلومات التوصيل
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: { xs: 1.5, sm: 2 }, pt: 0, mt: -0.5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {shippingAddress.fullName && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ color: 'text.secondary', fontSize: { xs: 18, sm: 20 } }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary" fontWeight="bold">الاسم الكامل</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {shippingAddress.fullName}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {shippingAddress.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon sx={{ color: 'text.secondary', fontSize: { xs: 18, sm: 20 } }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary" fontWeight="bold">رقم الهاتف</Typography>
                          <Typography variant="body2" fontWeight="bold" dir="ltr" sx={{ textAlign: 'right' }}>
                            {shippingAddress.phone}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {order.profiles?.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon sx={{ color: 'text.secondary', fontSize: { xs: 18, sm: 20 } }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary" fontWeight="bold">البريد الإلكتروني</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {order.profiles.email}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {shippingAddress.governorate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationCityIcon sx={{ color: 'text.secondary', fontSize: { xs: 18, sm: 20 } }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary" fontWeight="bold">المحافظة</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {shippingAddress.governorate}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    
                    {shippingAddress.address && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <LocationOnIcon sx={{ color: 'text.secondary', fontSize: { xs: 18, sm: 20 }, mt: 0.5 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary" fontWeight="bold">العنوان</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {shippingAddress.address}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {shippingPrice > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalShippingIcon sx={{ color: 'text.secondary', fontSize: { xs: 18, sm: 20 } }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary" fontWeight="bold">تكلفة الشحن</Typography>
                          <Typography variant="body2" fontWeight="bold" color="info.main">
                            {shippingPrice.toFixed(2)} جنيه
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {shippingAddress.notes && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <NotesIcon sx={{ color: 'text.secondary', fontSize: { xs: 18, sm: 20 }, mt: 0.5 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary" fontWeight="bold">ملاحظات إضافية</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {shippingAddress.notes}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Paper>
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};

export default DesktopOrderDetailsView;